import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Badge,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Label,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import {
  Calculator,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Info,
  RefreshCw,
  Clock,
  Building2,
  Wallet,
  Calendar,
  Percent,
  DollarSign,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { holidaysApi } from "@/services/api";

// Constantes
const DIAS_UTEIS_ANO = 252;
const DIAS_CORRIDOS_ANO = 365;
const DIAS_UTEIS_SEMANA = 5;
const DIAS_NAO_UTEIS_SEMANA = 2;

interface SimulationParams {
  cdi: number;
  aum: number;
  turnover: number;
  offHours: number;
  gapHours: number;
  cdiRecebido: number;
  cdiPago: number;
}

interface SimulationResults {
  taxaRecebida: number;
  taxaPaga: number;
  fatorDiarioRecebe: number;
  fatorDiarioPaga: number;
  ganhoDiaUtil: number;
  perdaDiaNaoUtil: number;
  diasParaCompensar: number;
  ganhoSemanal: number;
  receitaAnual: number;
  despesaAnual: number;
  spreadBruto: number;
  volumeMovMes: number;
  volumeForaHorario: number;
  custoGapMes: number;
  custoGapAnual: number;
  spreadLiquido: number;
  margemLiquida: number;
  breakEvenTurnover: number;
  breakEvenOffHours: number;
  breakEvenGap: number;
  margemSeguranca: number;
  aumValor: number;
}

interface Cenario {
  nome: string;
  aum: number;
  turnover: number;
  offHours: number;
  gapHours: number;
  highlight?: boolean;
}

function calcularFatorDiario(taxaAnual: number, base: number): number {
  return Math.pow(1 + taxaAnual, 1 / base) - 1;
}

function formatarMoeda(valor: number): string {
  if (Math.abs(valor) >= 1000000000) {
    return `R$ ${(valor / 1000000000).toFixed(2)}B`;
  } else if (Math.abs(valor) >= 1000000) {
    return `R$ ${(valor / 1000000).toFixed(2)}MM`;
  } else if (Math.abs(valor) >= 1000) {
    return `R$ ${(valor / 1000).toFixed(1)}K`;
  }
  return `R$ ${valor.toFixed(2)}`;
}

// Formatar moeda completa (sem abreviações K, MM, B)
function formatarMoedaCompleta(valor: number): string {
  return valor.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatarPercentual(valor: number): string {
  return `${(valor * 100).toFixed(5)}%`;
}

function calcularResultados(params: SimulationParams): SimulationResults {
  const { cdi, aum, turnover, offHours, gapHours, cdiRecebido, cdiPago } = params;

  // Taxas
  const taxaRecebida = (cdiRecebido / 100) * cdi;
  const taxaPaga = (cdiPago / 100) * cdi;

  // Fatores diários
  const fatorDiarioRecebe = calcularFatorDiario(taxaRecebida, DIAS_UTEIS_ANO);
  const fatorDiarioPaga = calcularFatorDiario(taxaPaga, DIAS_CORRIDOS_ANO);

  // Ganho/Perda por tipo de dia
  const ganhoDiaUtil = fatorDiarioRecebe - fatorDiarioPaga;
  const perdaDiaNaoUtil = fatorDiarioPaga;

  // Compensação
  const diasParaCompensar = perdaDiaNaoUtil / ganhoDiaUtil;
  const ganhoSemanal =
    DIAS_UTEIS_SEMANA * ganhoDiaUtil - DIAS_NAO_UTEIS_SEMANA * perdaDiaNaoUtil;

  // Receita/Despesa anual sobre saldo estável
  const aumValor = aum * 1000000;
  const receitaAnual = aumValor * taxaRecebida;
  const despesaAnual = aumValor * taxaPaga;
  const spreadBruto = receitaAnual - despesaAnual;

  // Custo do Gap de horário
  const volumeMovMes = aumValor * (turnover / 100);
  const volumeForaHorario = volumeMovMes * (offHours / 100);
  const taxaPorHora = fatorDiarioPaga / 24;
  const custoGapMes = volumeForaHorario * taxaPorHora * gapHours;
  const custoGapAnual = custoGapMes * 12;

  // Spread líquido
  const spreadLiquido = spreadBruto - custoGapAnual;
  const margemLiquida = spreadLiquido / aumValor;

  // Break-even calculations
  const custoGapPorTurnover =
    aumValor * 0.01 * (offHours / 100) * taxaPorHora * gapHours * 12;
  const breakEvenTurnover =
    custoGapPorTurnover > 0 ? spreadBruto / custoGapPorTurnover : 999;

  const custoGapPorOffHours = volumeMovMes * 0.01 * taxaPorHora * gapHours * 12;
  const breakEvenOffHours =
    custoGapPorOffHours > 0 ? spreadBruto / custoGapPorOffHours : 999;

  const custoGapPorHora = volumeForaHorario * taxaPorHora * 12;
  const breakEvenGap =
    custoGapPorHora > 0 ? spreadBruto / custoGapPorHora : 999;

  // Margem de segurança
  const margemSeguranca = ((spreadBruto - custoGapAnual) / spreadBruto) * 100;

  return {
    taxaRecebida,
    taxaPaga,
    fatorDiarioRecebe,
    fatorDiarioPaga,
    ganhoDiaUtil,
    perdaDiaNaoUtil,
    diasParaCompensar,
    ganhoSemanal,
    receitaAnual,
    despesaAnual,
    spreadBruto,
    volumeMovMes,
    volumeForaHorario,
    custoGapMes,
    custoGapAnual,
    spreadLiquido,
    margemLiquida,
    breakEvenTurnover,
    breakEvenOffHours,
    breakEvenGap,
    margemSeguranca,
    aumValor,
  };
}

const cenariosPredefinidos: Record<string, Omit<Cenario, "nome">> = {
  ideal: { aum: 500, turnover: 10, offHours: 20, gapHours: 12 },
  base: { aum: 500, turnover: 30, offHours: 40, gapHours: 18 },
  pessimista: { aum: 500, turnover: 60, offHours: 60, gapHours: 30 },
  stress: { aum: 500, turnover: 80, offHours: 80, gapHours: 48 },
};

// ============================================
// SIMULAÇÃO DO CLIENTE (EMPRESA)
// ============================================

interface ClientSimulationParams {
  valorAplicado: number;
  diasAplicado: number;
  dataBase: string; // Data inicial da aplicação (YYYY-MM-DD)
  cdiAnual: number;
  percentualCdiPago: number;
}

interface ProductResult {
  nome: string;
  descricao: string;
  rentabilidadeCdi: string;
  rendimentoBruto: number;
  iof: number;
  rendimentoLiquido: number;
  valorFinal: number;
  percentualDoCdi: number;
  disponibilidade: string;
  lastro: string;
  vantagem?: string;
  desvantagem?: string;
  destaque?: boolean;
  // Novos campos para mostrar a lógica do cálculo
  taxaDiaria: number;        // Taxa diária do produto
  base: number;              // Base de cálculo (252 ou 365)
  diasRendimento: number;    // Quantos dias efetivamente rendem no período
}

interface ClientSimulationResults {
  // Rendimento na stablecoin
  rendimentoBruto: number;
  valorFinal: number;
  rentabilidadePercentual: number;
  rentabilidadeAnualizada: number;

  // Comparação com CDI
  rendimentoCdi100: number;
  percentualDoCdi: number;

  // Comparação com outras aplicações
  rendimentoPoupanca: number;
  rendimentoCdb90: number;
  rendimentoCdb100: number;
  rendimentoCdb110: number;

  // Detalhes do cálculo
  taxaDiaria: number;
  diasUteis: number;
  diasNaoUteis: number;
  dataBase: string;
  dataFinal: string;

  // Comparativo de produtos
  produtos: ProductResult[];
  melhorProduto: string;
}

// Calcula dias úteis reais considerando fins de semana e feriados
function calcularDiasUteisReais(
  dataBase: string,
  diasTotais: number,
  feriados: Set<string>
): { uteis: number; naoUteis: number; dataFinal: string } {
  const inicio = new Date(dataBase + "T12:00:00"); // Usar meio-dia para evitar problemas de timezone
  let uteis = 0;
  let naoUteis = 0;
  let dataAtual = new Date(inicio);

  for (let i = 0; i < diasTotais; i++) {
    const diaSemana = dataAtual.getDay(); // 0 = domingo, 6 = sábado
    const dataStr = dataAtual.toISOString().split("T")[0];

    const isFimDeSemana = diaSemana === 0 || diaSemana === 6;
    const isFeriado = feriados.has(dataStr);

    if (isFimDeSemana || isFeriado) {
      naoUteis++;
    } else {
      uteis++;
    }

    // Avançar para o próximo dia
    dataAtual.setDate(dataAtual.getDate() + 1);
  }

  const dataFinal = new Date(inicio);
  dataFinal.setDate(dataFinal.getDate() + diasTotais - 1);

  return {
    uteis,
    naoUteis,
    dataFinal: dataFinal.toISOString().split("T")[0]
  };
}


// Tabela de IOF regressivo (% sobre o rendimento)
const TABELA_IOF = [
  96, 93, 90, 86, 83, 80, 76, 73, 70, 66, // dias 1-10
  63, 60, 56, 53, 50, 46, 43, 40, 36, 33, // dias 11-20
  30, 26, 23, 20, 16, 13, 10, 6, 3, 0,    // dias 21-30
];

function calcularIOF(rendimento: number, dias: number): number {
  if (dias <= 0 || rendimento <= 0) return 0;
  if (dias > 30) return 0; // Isento após 30 dias
  const aliquota = TABELA_IOF[dias - 1] / 100;
  return rendimento * aliquota;
}

function calcularRendimentoCliente(
  params: ClientSimulationParams,
  feriados: Set<string> = new Set()
): ClientSimulationResults {
  const { valorAplicado, diasAplicado, dataBase, cdiAnual, percentualCdiPago } = params;

  // Taxa que a stablecoin paga (base 365)
  const taxaAnualPaga = (percentualCdiPago / 100) * cdiAnual;
  const taxaDiaria = Math.pow(1 + taxaAnualPaga, 1 / DIAS_CORRIDOS_ANO) - 1;

  // Rendimento na stablecoin (365 dias corridos - rende todos os dias)
  const fatorAcumulado = Math.pow(1 + taxaDiaria, diasAplicado);
  const valorFinal = valorAplicado * fatorAcumulado;
  const rendimentoBruto = valorFinal - valorAplicado;

  // Rentabilidade
  const rentabilidadePercentual = (rendimentoBruto / valorAplicado) * 100;
  const rentabilidadeAnualizada = (Math.pow(fatorAcumulado, DIAS_CORRIDOS_ANO / diasAplicado) - 1) * 100;

  // CDI 100% (base 252 - só dias úteis) - cálculo real baseado na data
  const diasCalculados = calcularDiasUteisReais(dataBase, diasAplicado, feriados);
  const { uteis: diasUteis, naoUteis: diasNaoUteis, dataFinal } = diasCalculados;

  const taxaDiariaCdi = Math.pow(1 + cdiAnual, 1 / DIAS_UTEIS_ANO) - 1;
  const fatorCdi100 = Math.pow(1 + taxaDiariaCdi, diasUteis);
  const rendimentoCdi100 = valorAplicado * (fatorCdi100 - 1);

  // Percentual do CDI que o cliente está recebendo
  const percentualDoCdi = rendimentoCdi100 > 0 ? (rendimentoBruto / rendimentoCdi100) * 100 : 0;

  // Poupança (aproximadamente 6.17% a.a. ou 70% Selic quando Selic > 8.5%)
  const taxaPoupanca = Math.min(cdiAnual * 0.7, 0.0617);
  const fatorPoupanca = Math.pow(1 + taxaPoupanca, diasAplicado / DIAS_CORRIDOS_ANO);
  const rendimentoPoupanca = valorAplicado * (fatorPoupanca - 1);

  // CDBs (base 252)
  const calcularCdb = (percentCdi: number) => {
    const taxaCdb = (percentCdi / 100) * cdiAnual;
    const taxaDiariaCdb = Math.pow(1 + taxaCdb, 1 / DIAS_UTEIS_ANO) - 1;
    const fatorCdb = Math.pow(1 + taxaDiariaCdb, diasUteis);
    return valorAplicado * (fatorCdb - 1);
  };

  // ============================================
  // COMPARATIVO DE PRODUTOS
  // ============================================
  const produtos: ProductResult[] = [];

  // 1. STABLECOIN LIQI (PIX) - 95% CDI, sem IOF, 24x7
  const stablecoinRendimento = rendimentoBruto;
  produtos.push({
    nome: "Stablecoin Liqi",
    descricao: "Pix Stablecoin",
    rentabilidadeCdi: `${percentualCdiPago}% CDI`,
    rendimentoBruto: stablecoinRendimento,
    iof: 0,
    rendimentoLiquido: stablecoinRendimento,
    valorFinal: valorAplicado + stablecoinRendimento,
    percentualDoCdi: percentualDoCdi,
    disponibilidade: "24x7x365",
    lastro: "Títulos Públicos",
    vantagem: "Liquidez imediata, sem IOF, rende fins de semana",
    destaque: true,
    taxaDiaria: taxaDiaria,
    base: DIAS_CORRIDOS_ANO,
    diasRendimento: diasAplicado, // Rende todos os dias (365)
  });

  // 2. COMPROMISSADA (Mesa Operação) - 80% CDI, sem IOF, horário comercial
  const compromissadaPercent = 80;
  const compromissadaRendimentoBruto = calcularCdb(compromissadaPercent);
  const taxaDiariaCompromissada = Math.pow(1 + (compromissadaPercent / 100) * cdiAnual, 1 / DIAS_UTEIS_ANO) - 1;
  produtos.push({
    nome: "Compromissada",
    descricao: "Mesa Operação",
    rentabilidadeCdi: `${compromissadaPercent}% CDI`,
    rendimentoBruto: compromissadaRendimentoBruto,
    iof: 0,
    rendimentoLiquido: compromissadaRendimentoBruto,
    valorFinal: valorAplicado + compromissadaRendimentoBruto,
    percentualDoCdi: rendimentoCdi100 > 0 ? (compromissadaRendimentoBruto / rendimentoCdi100) * 100 : 0,
    disponibilidade: "09h às 18h - Dias úteis",
    lastro: "Cesta de Títulos",
    desvantagem: "Requer mesa de operação, não rende fins de semana",
    taxaDiaria: taxaDiariaCompromissada,
    base: DIAS_UTEIS_ANO,
    diasRendimento: diasUteis, // Só dias úteis (252)
  });

  // 3. CDB (Internet Banking) - 90% CDI, com IOF até 30 dias
  const cdbPercent = 90;
  const cdbRendimentoBruto = calcularCdb(cdbPercent);
  const cdbIof = calcularIOF(cdbRendimentoBruto, diasAplicado);
  const cdbRendimentoLiquido = cdbRendimentoBruto - cdbIof;
  const taxaDiariaCdb = Math.pow(1 + (cdbPercent / 100) * cdiAnual, 1 / DIAS_UTEIS_ANO) - 1;
  produtos.push({
    nome: "CDB",
    descricao: "Internet Banking",
    rentabilidadeCdi: `${cdbPercent}% CDI`,
    rendimentoBruto: cdbRendimentoBruto,
    iof: cdbIof,
    rendimentoLiquido: cdbRendimentoLiquido,
    valorFinal: valorAplicado + cdbRendimentoLiquido,
    percentualDoCdi: rendimentoCdi100 > 0 ? (cdbRendimentoLiquido / rendimentoCdi100) * 100 : 0,
    disponibilidade: "09h às 18h - Dias úteis",
    lastro: "Títulos Públicos",
    desvantagem: diasAplicado <= 30 ? `IOF de ${TABELA_IOF[Math.min(diasAplicado, 30) - 1]}% sobre rendimento` : undefined,
    taxaDiaria: taxaDiariaCdb,
    base: DIAS_UTEIS_ANO,
    diasRendimento: diasUteis, // Só dias úteis (252)
  });

  // 4. APLICAÇÃO AUTOMÁTICA - 15% CDI (média), com IOF até 30 dias
  const autoPercent = 15; // Média entre 10-20%
  const autoRendimentoBruto = calcularCdb(autoPercent);
  const autoIof = calcularIOF(autoRendimentoBruto, diasAplicado);
  const autoRendimentoLiquido = autoRendimentoBruto - autoIof;
  const taxaDiariaAuto = Math.pow(1 + (autoPercent / 100) * cdiAnual, 1 / DIAS_UTEIS_ANO) - 1;
  produtos.push({
    nome: "Aplicação Automática",
    descricao: "Conta Corrente",
    rentabilidadeCdi: "10 a 20% CDI",
    rendimentoBruto: autoRendimentoBruto,
    iof: autoIof,
    rendimentoLiquido: autoRendimentoLiquido,
    valorFinal: valorAplicado + autoRendimentoLiquido,
    percentualDoCdi: rendimentoCdi100 > 0 ? (autoRendimentoLiquido / rendimentoCdi100) * 100 : 0,
    disponibilidade: "09h às 18h - Dias úteis",
    lastro: "Títulos Públicos",
    desvantagem: "Rentabilidade muito baixa",
    taxaDiaria: taxaDiariaAuto,
    base: DIAS_UTEIS_ANO,
    diasRendimento: diasUteis, // Só dias úteis (252)
  });

  // 5. POUPANÇA (referência)
  const poupancaIof = 0; // Poupança não tem IOF
  const taxaDiariaPoupanca = Math.pow(1 + taxaPoupanca, 1 / DIAS_CORRIDOS_ANO) - 1;
  produtos.push({
    nome: "Poupança",
    descricao: "Referência",
    rentabilidadeCdi: "~70% CDI",
    rendimentoBruto: rendimentoPoupanca,
    iof: poupancaIof,
    rendimentoLiquido: rendimentoPoupanca,
    valorFinal: valorAplicado + rendimentoPoupanca,
    percentualDoCdi: rendimentoCdi100 > 0 ? (rendimentoPoupanca / rendimentoCdi100) * 100 : 0,
    disponibilidade: "Aniversário mensal",
    lastro: "Diversos",
    desvantagem: "Rende apenas no aniversário, liquidez reduzida",
    taxaDiaria: taxaDiariaPoupanca,
    base: DIAS_CORRIDOS_ANO,
    diasRendimento: diasAplicado, // Base 365 mas só rende no aniversário
  });

  // Ordenar por rendimento líquido (melhor primeiro)
  produtos.sort((a, b) => b.rendimentoLiquido - a.rendimentoLiquido);

  // Identificar o melhor produto
  const melhorProduto = produtos[0]?.nome || "";

  return {
    rendimentoBruto,
    valorFinal,
    rentabilidadePercentual,
    rentabilidadeAnualizada,
    rendimentoCdi100,
    percentualDoCdi,
    rendimentoPoupanca,
    rendimentoCdb90: calcularCdb(90),
    rendimentoCdb100: calcularCdb(100),
    rendimentoCdb110: calcularCdb(110),
    taxaDiaria,
    diasUteis,
    diasNaoUteis,
    dataBase,
    dataFinal,
    produtos,
    melhorProduto,
  };
}

export function CdiSimulationPage() {
  const [activeTab, setActiveTab] = useState("operacional");

  // ============================================
  // ESTADO - SIMULAÇÃO OPERACIONAL
  // ============================================
  const [params, setParams] = useState<SimulationParams>({
    cdi: 14.5,
    aum: 500,
    turnover: 30,
    offHours: 40,
    gapHours: 18,
    cdiRecebido: 99,
    cdiPago: 95,
  });

  const [results, setResults] = useState<SimulationResults | null>(null);

  // ============================================
  // ESTADO - SIMULAÇÃO DO CLIENTE
  // ============================================
  const [clientParams, setClientParams] = useState<ClientSimulationParams>({
    valorAplicado: 100000,
    diasAplicado: 30,
    dataBase: new Date().toISOString().split("T")[0], // Data de hoje
    cdiAnual: 0.145, // 14.5%
    percentualCdiPago: 95,
  });

  const [clientResults, setClientResults] = useState<ClientSimulationResults | null>(null);

  // ============================================
  // BUSCAR FERIADOS
  // ============================================
  const anoBase = new Date(clientParams.dataBase).getFullYear();
  const anoFinal = new Date(
    new Date(clientParams.dataBase).getTime() + clientParams.diasAplicado * 24 * 60 * 60 * 1000
  ).getFullYear();

  // Buscar feriados dos anos relevantes
  const { data: feriadosAnoBase } = useQuery({
    queryKey: ["holidays", anoBase],
    queryFn: () => holidaysApi.getByYear(anoBase),
    staleTime: 1000 * 60 * 60, // 1 hora
  });

  const { data: feriadosAnoFinal } = useQuery({
    queryKey: ["holidays", anoFinal],
    queryFn: () => holidaysApi.getByYear(anoFinal),
    enabled: anoFinal !== anoBase, // Só busca se for ano diferente
    staleTime: 1000 * 60 * 60,
  });

  // Combinar feriados em um Set para lookup rápido
  const feriadosSet = useMemo(() => {
    const set = new Set<string>();
    if (feriadosAnoBase) {
      feriadosAnoBase.forEach((f: { date: string }) => set.add(f.date.split("T")[0]));
    }
    if (feriadosAnoFinal && anoFinal !== anoBase) {
      feriadosAnoFinal.forEach((f: { date: string }) => set.add(f.date.split("T")[0]));
    }
    return set;
  }, [feriadosAnoBase, feriadosAnoFinal, anoBase, anoFinal]);

  // ============================================
  // CÁLCULOS
  // ============================================
  const calcular = useCallback(() => {
    const r = calcularResultados({
      ...params,
      cdi: params.cdi / 100,
    });
    setResults(r);
  }, [params]);

  const calcularCliente = useCallback(() => {
    const r = calcularRendimentoCliente(
      {
        ...clientParams,
        cdiAnual: params.cdi / 100, // Sincroniza com o CDI da aba operacional
      },
      feriadosSet
    );
    setClientResults(r);
  }, [clientParams, params.cdi, feriadosSet]);

  useEffect(() => {
    calcular();
  }, [calcular]);

  useEffect(() => {
    calcularCliente();
  }, [calcularCliente]);

  const handleParamChange = (key: keyof SimulationParams, value: number) => {
    setParams((prev) => ({ ...prev, [key]: value }));
  };

  const setScenario = (tipo: string) => {
    const cenario = cenariosPredefinidos[tipo];
    if (cenario) {
      setParams((prev) => ({
        ...prev,
        aum: cenario.aum,
        turnover: cenario.turnover,
        offHours: cenario.offHours,
        gapHours: cenario.gapHours,
      }));
    }
  };

  const gerarRecomendacoes = () => {
    if (!results) return [];
    const recomendacoes: { tipo: "success" | "warning" | "info"; texto: string }[] = [];

    if (results.margemLiquida < 0.002) {
      recomendacoes.push({
        tipo: "warning",
        texto:
          "Margem muito baixa. Considere aumentar o spread (reduzir % CDI pago) ou buscar clientes com menor rotatividade.",
      });
    }

    if (params.offHours > 50) {
      recomendacoes.push({
        tipo: "info",
        texto:
          "Alta movimentação fora do horário. Considere incentivos para operações em horário comercial.",
      });
    }

    if (params.turnover > 60) {
      recomendacoes.push({
        tipo: "warning",
        texto:
          "Alta rotatividade corrói a margem. Busque clientes corporativos que mantêm saldo por mais tempo.",
      });
    }

    if (results.margemSeguranca > 50) {
      recomendacoes.push({
        tipo: "success",
        texto:
          "Operação saudável com boa margem de segurança. Cenário favorável para escalar.",
      });
    }

    if (params.gapHours > 24) {
      recomendacoes.push({
        tipo: "info",
        texto:
          "Gap alto sugere operações em fins de semana/feriados. Considere parceria com banco que ofereça operações estendidas.",
      });
    }

    return recomendacoes;
  };

  const gerarCenarios = (): Cenario[] => {
    return [
      { nome: "Ideal", aum: 500, turnover: 10, offHours: 20, gapHours: 12 },
      { nome: "Bom", aum: 500, turnover: 20, offHours: 30, gapHours: 16 },
      { nome: "Base", aum: 500, turnover: 30, offHours: 40, gapHours: 18 },
      { nome: "Difícil", aum: 500, turnover: 50, offHours: 50, gapHours: 24 },
      { nome: "Pessimista", aum: 500, turnover: 60, offHours: 60, gapHours: 30 },
      { nome: "Stress", aum: 500, turnover: 80, offHours: 80, gapHours: 48 },
      {
        nome: "Atual",
        aum: params.aum,
        turnover: params.turnover,
        offHours: params.offHours,
        gapHours: params.gapHours,
        highlight: true,
      },
    ];
  };

  const recomendacoes = gerarRecomendacoes();

  const handleClientParamChange = (key: keyof ClientSimulationParams, value: number) => {
    setClientParams((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Calculator className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Simulação CDI"
        description="Análise de viabilidade financeira e comparativo de produtos"
        stats={
          activeTab === "operacional" && results
            ? [
                {
                  value: `${(results.margemLiquida * 100).toFixed(2)}%`,
                  label: "Margem Líquida",
                },
                {
                  value: formatarMoeda(results.spreadLiquido),
                  label: "Spread Líquido/ano",
                },
              ]
            : activeTab === "cliente" && clientResults
              ? [
                  {
                    value: `${clientResults.percentualDoCdi.toFixed(1)}% CDI`,
                    label: "Rentabilidade Efetiva",
                  },
                  {
                    value: formatarMoeda(clientResults.rendimentoBruto),
                    label: "Rendimento Stablecoin",
                  },
                ]
              : undefined
        }
        actions={
          <Button onClick={activeTab === "operacional" ? calcular : calcularCliente} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Recalcular
          </Button>
        }
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="operacional" className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Visão Operacional
          </TabsTrigger>
          <TabsTrigger value="cliente" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Simulação Empresa
          </TabsTrigger>
        </TabsList>

        {/* ============================================ */}
        {/* ABA: VISÃO OPERACIONAL */}
        {/* ============================================ */}
        <TabsContent value="operacional" className="space-y-4 sm:space-y-6 mt-6">

      {/* Estrutura da Operação - Cards no padrão */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="font-medium">Lastro (LFT)</p>
                <p className="text-lg font-bold text-green-500">
                  Recebe {params.cdiRecebido}% CDI
                </p>
              </div>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              Base 252 dias úteis
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="font-medium">Stablecoin (Passivo)</p>
                <p className="text-lg font-bold text-red-500">
                  Paga {params.cdiPago}% CDI
                </p>
              </div>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              Base 365 dias corridos (24/7)
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <p className="font-medium">Reserva Banco</p>
                <p className="text-lg font-bold text-yellow-500">
                  100MM (custo zero)
                </p>
              </div>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              Rende 99% CDI, repassa 100%
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Parâmetros */}
        <div className="lg:col-span-1 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Parâmetros</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* CDI */}
              <div className="space-y-2">
                <div className="flex justify-between">
                  <label className="text-sm font-medium">CDI Anual</label>
                  <span className="text-sm font-semibold">{params.cdi.toFixed(2)}%</span>
                </div>
                <Input
                  type="range"
                  min={8}
                  max={20}
                  step={0.1}
                  value={params.cdi}
                  onChange={(e) => handleParamChange("cdi", parseFloat(e.target.value))}
                  className="cursor-pointer"
                />
              </div>

              {/* AUM */}
              <div className="space-y-2">
                <div className="flex justify-between">
                  <label className="text-sm font-medium">Volume Total (AUM)</label>
                  <span className="text-sm font-semibold">R$ {params.aum}MM</span>
                </div>
                <Input
                  type="range"
                  min={50}
                  max={2000}
                  step={50}
                  value={params.aum}
                  onChange={(e) => handleParamChange("aum", parseFloat(e.target.value))}
                  className="cursor-pointer"
                />
              </div>

              {/* Turnover */}
              <div className="space-y-2">
                <div className="flex justify-between">
                  <label className="text-sm font-medium">Rotatividade Mensal</label>
                  <span className="text-sm font-semibold">{params.turnover}%</span>
                </div>
                <Input
                  type="range"
                  min={5}
                  max={100}
                  step={5}
                  value={params.turnover}
                  onChange={(e) =>
                    handleParamChange("turnover", parseFloat(e.target.value))
                  }
                  className="cursor-pointer"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Baixa</span>
                  <span>Alta</span>
                </div>
              </div>

              {/* % Fora do Horário */}
              <div className="space-y-2">
                <div className="flex justify-between">
                  <label className="text-sm font-medium">% Fora do Horário</label>
                  <span className="text-sm font-semibold">{params.offHours}%</span>
                </div>
                <Input
                  type="range"
                  min={0}
                  max={100}
                  step={5}
                  value={params.offHours}
                  onChange={(e) =>
                    handleParamChange("offHours", parseFloat(e.target.value))
                  }
                  className="cursor-pointer"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Empresas tradicionais</span>
                  <span>Crypto-native</span>
                </div>
              </div>

              {/* Gap médio */}
              <div className="space-y-2">
                <div className="flex justify-between">
                  <label className="text-sm font-medium">Gap Médio (horas)</label>
                  <span className="text-sm font-semibold">{params.gapHours}h</span>
                </div>
                <Input
                  type="range"
                  min={6}
                  max={72}
                  step={2}
                  value={params.gapHours}
                  onChange={(e) =>
                    handleParamChange("gapHours", parseFloat(e.target.value))
                  }
                  className="cursor-pointer"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>6h (noite)</span>
                  <span>72h (feriado)</span>
                </div>
              </div>

              {/* Taxas */}
              <div className="border-t pt-4 space-y-4">
                <label className="text-sm font-medium">Taxas da Operação</label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">% CDI Recebido</label>
                    <Input
                      type="number"
                      value={params.cdiRecebido}
                      onChange={(e) =>
                        handleParamChange("cdiRecebido", parseFloat(e.target.value))
                      }
                      min={90}
                      max={100}
                      step={0.5}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">% CDI Pago</label>
                    <Input
                      type="number"
                      value={params.cdiPago}
                      onChange={(e) =>
                        handleParamChange("cdiPago", parseFloat(e.target.value))
                      }
                      min={80}
                      max={100}
                      step={0.5}
                    />
                  </div>
                </div>
              </div>

              {/* Cenários Rápidos */}
              <div className="border-t pt-4 space-y-3">
                <label className="text-sm font-medium">Cenários Pré-definidos</label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setScenario("ideal")}
                  >
                    Ideal
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setScenario("base")}
                  >
                    Base
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setScenario("pessimista")}
                  >
                    Pessimista
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setScenario("stress")}
                  >
                    Stress
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Resultados */}
        <div className="lg:col-span-2 space-y-4">
          {/* Resumo Principal */}
          {results && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Resultado Anual</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-muted p-4 rounded-lg text-center">
                      <p className="text-sm text-muted-foreground mb-1">
                        Spread Bruto
                      </p>
                      <p className="text-xl font-bold text-green-500">
                        {formatarMoeda(results.spreadBruto)}
                      </p>
                    </div>
                    <div className="bg-muted p-4 rounded-lg text-center">
                      <p className="text-sm text-muted-foreground mb-1">
                        Custo do Gap
                      </p>
                      <p className="text-xl font-bold text-red-500">
                        {formatarMoeda(results.custoGapAnual)}
                      </p>
                    </div>
                    <div className="bg-muted p-4 rounded-lg text-center">
                      <p className="text-sm text-muted-foreground mb-1">
                        Spread Líquido
                      </p>
                      <p
                        className={`text-xl font-bold ${
                          results.spreadLiquido >= 0 ? "text-green-500" : "text-red-500"
                        }`}
                      >
                        {formatarMoeda(results.spreadLiquido)}
                      </p>
                    </div>
                    <div className="bg-muted p-4 rounded-lg text-center">
                      <p className="text-sm text-muted-foreground mb-1">
                        Margem Líquida
                      </p>
                      <p
                        className={`text-xl font-bold ${
                          results.margemLiquida >= 0 ? "text-green-500" : "text-red-500"
                        }`}
                      >
                        {(results.margemLiquida * 100).toFixed(3)}%
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Detalhamento */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Detalhamento do Cálculo</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Lado Receita */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-green-500 font-medium">
                        <TrendingUp className="w-4 h-4" />
                        Receita (Lastro LFT)
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Taxa anual (base 252):
                          </span>
                          <span>{(results.taxaRecebida * 100).toFixed(3)}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Fator diário (dia útil):
                          </span>
                          <span>{formatarPercentual(results.fatorDiarioRecebe)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Receita anual bruta:
                          </span>
                          <span className="text-green-500 font-medium">
                            {formatarMoeda(results.receitaAnual)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Lado Despesa */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-red-500 font-medium">
                        <TrendingDown className="w-4 h-4" />
                        Despesa (Stablecoin)
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Taxa anual (base 365):
                          </span>
                          <span>{(results.taxaPaga * 100).toFixed(3)}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Fator diário (dia corrido):
                          </span>
                          <span>{formatarPercentual(results.fatorDiarioPaga)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Despesa anual:</span>
                          <span className="text-red-500 font-medium">
                            {formatarMoeda(results.despesaAnual)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Análise do Gap */}
                  <div className="mt-6 pt-6 border-t">
                    <div className="flex items-center gap-2 text-yellow-500 font-medium mb-3">
                      <AlertTriangle className="w-4 h-4" />
                      Análise do Gap de Horário
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">
                          Volume movimentado/mês
                        </p>
                        <p className="font-medium">
                          {formatarMoeda(results.volumeMovMes)}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">
                          Movimentação fora horário
                        </p>
                        <p className="font-medium">
                          {formatarMoeda(results.volumeForaHorario)}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Gap médio</p>
                        <p className="font-medium">{params.gapHours} horas</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Custo do gap/mês</p>
                        <p className="font-medium text-red-500">
                          {formatarMoeda(results.custoGapMes)}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Mecanismo de Compensação */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Mecanismo de Compensação</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="bg-muted p-4 rounded-lg">
                      <p className="text-sm text-green-500 mb-1">
                        Ganho por dia útil
                      </p>
                      <p className="text-xl font-bold">
                        {formatarPercentual(results.ganhoDiaUtil)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Diferença entre receber 252 e pagar 365
                      </p>
                    </div>
                    <div className="bg-muted p-4 rounded-lg">
                      <p className="text-sm text-red-500 mb-1">
                        Perda por dia não-útil
                      </p>
                      <p className="text-xl font-bold">
                        {formatarPercentual(results.perdaDiaNaoUtil)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Paga juros, não recebe lastro
                      </p>
                    </div>
                    <div className="bg-muted p-4 rounded-lg">
                      <p className="text-sm text-blue-500 mb-1">
                        Dias úteis p/ compensar 1 dia
                      </p>
                      <p className="text-xl font-bold">
                        {results.diasParaCompensar.toFixed(2)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Proporção de recuperação
                      </p>
                    </div>
                  </div>

                  <div className="bg-muted p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span>Saldo semanal (5 úteis + 2 não-úteis):</span>
                      <span
                        className={`text-xl font-bold ${
                          results.ganhoSemanal >= 0 ? "text-green-500" : "text-red-500"
                        }`}
                      >
                        {results.ganhoSemanal >= 0 ? "+" : ""}
                        {formatarPercentual(results.ganhoSemanal)}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">
                      {results.ganhoSemanal >= 0
                        ? "O spread estrutural é positivo: os dias úteis compensam os fins de semana."
                        : "Atenção: o spread estrutural é negativo. Revise as taxas."}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Break-even Analysis */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Análise de Break-even</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <label className="text-sm font-medium">Pontos Críticos</label>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground text-sm">
                            Rotatividade máxima (break-even):
                          </span>
                          <Badge variant="outline">
                            {Math.min(
                              results.breakEvenTurnover * params.turnover,
                              999
                            ).toFixed(0)}
                            %
                          </Badge>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground text-sm">
                            % fora horário máximo:
                          </span>
                          <Badge variant="outline">
                            {Math.min(
                              results.breakEvenOffHours * params.offHours,
                              100
                            ).toFixed(0)}
                            %
                          </Badge>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground text-sm">
                            Gap máximo tolerável:
                          </span>
                          <Badge variant="outline">
                            {Math.min(
                              results.breakEvenGap * params.gapHours,
                              168
                            ).toFixed(0)}
                            h
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <label className="text-sm font-medium">Status da Operação</label>
                      <div
                        className={`p-4 rounded-lg text-center ${
                          results.spreadLiquido > 0
                            ? "bg-green-50 dark:bg-green-900/20"
                            : "bg-red-50 dark:bg-red-900/20"
                        }`}
                      >
                        <div className="mb-2">
                          {results.spreadLiquido > 0 ? (
                            <CheckCircle className="w-10 h-10 mx-auto text-green-500" />
                          ) : (
                            <AlertTriangle className="w-10 h-10 mx-auto text-red-500" />
                          )}
                        </div>
                        <p
                          className={`text-lg font-bold ${
                            results.spreadLiquido > 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                          }`}
                        >
                          {results.spreadLiquido > 0
                            ? "Operação Viável"
                            : "Operação Inviável"}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {results.spreadLiquido > 0
                            ? `Margem de segurança: ${results.margemSeguranca.toFixed(0)}%`
                            : `Prejuízo: ${formatarMoeda(Math.abs(results.spreadLiquido))}/ano`}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Tabela de Cenários */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Comparativo de Cenários</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-muted">
                        <tr>
                          <th className="text-left p-4 font-medium text-sm">Cenário</th>
                          <th className="text-right p-4 font-medium text-sm">AUM</th>
                          <th className="text-right p-4 font-medium text-sm">Turnover</th>
                          <th className="text-right p-4 font-medium text-sm">% Fora</th>
                          <th className="text-right p-4 font-medium text-sm">Spread Bruto</th>
                          <th className="text-right p-4 font-medium text-sm">Custo Gap</th>
                          <th className="text-right p-4 font-medium text-sm">Spread Líq.</th>
                          <th className="text-right p-4 font-medium text-sm">Margem</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {gerarCenarios().map((c) => {
                          const r = calcularResultados({
                            cdi: params.cdi / 100,
                            aum: c.aum,
                            turnover: c.turnover,
                            offHours: c.offHours,
                            gapHours: c.gapHours,
                            cdiRecebido: params.cdiRecebido,
                            cdiPago: params.cdiPago,
                          });

                          return (
                            <tr
                              key={c.nome}
                              className={`hover:bg-muted/50 ${c.highlight ? "bg-muted/30" : ""}`}
                            >
                              <td className="p-4 text-sm font-medium">
                                {c.nome}
                                {c.highlight && (
                                  <Badge variant="secondary" className="ml-2">
                                    Atual
                                  </Badge>
                                )}
                              </td>
                              <td className="p-4 text-sm text-right">R$ {c.aum}MM</td>
                              <td className="p-4 text-sm text-right">{c.turnover}%</td>
                              <td className="p-4 text-sm text-right">{c.offHours}%</td>
                              <td className="p-4 text-sm text-right text-green-500">
                                {formatarMoeda(r.spreadBruto)}
                              </td>
                              <td className="p-4 text-sm text-right text-red-500">
                                {formatarMoeda(r.custoGapAnual)}
                              </td>
                              <td
                                className={`p-4 text-sm text-right font-medium ${
                                  r.spreadLiquido >= 0 ? "text-green-500" : "text-red-500"
                                }`}
                              >
                                {formatarMoeda(r.spreadLiquido)}
                              </td>
                              <td
                                className={`p-4 text-sm text-right ${
                                  r.margemLiquida >= 0 ? "text-green-500" : "text-red-500"
                                }`}
                              >
                                {(r.margemLiquida * 100).toFixed(2)}%
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Recomendações */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recomendações</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {recomendacoes.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">
                      Parâmetros dentro do esperado.
                    </p>
                  ) : (
                    recomendacoes.map((rec, idx) => (
                      <div
                        key={idx}
                        className={`p-4 rounded-lg flex items-start gap-3 ${
                          rec.tipo === "success"
                            ? "bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-400"
                            : rec.tipo === "warning"
                              ? "bg-yellow-50 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400"
                              : "bg-blue-50 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
                        }`}
                      >
                        {rec.tipo === "success" ? (
                          <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        ) : rec.tipo === "warning" ? (
                          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        ) : (
                          <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        )}
                        <span>{rec.texto}</span>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>

        </TabsContent>

        {/* ============================================ */}
        {/* ABA: SIMULAÇÃO EMPRESA (CLIENTE) */}
        {/* ============================================ */}
        <TabsContent value="cliente" className="space-y-4 sm:space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Parâmetros do Cliente */}
            <div className="lg:col-span-1 space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Wallet className="w-5 h-5" />
                    Parâmetros da Aplicação
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Data Base */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Data Base da Aplicação
                    </Label>
                    <Input
                      type="date"
                      value={clientParams.dataBase}
                      onChange={(e) =>
                        setClientParams((prev) => ({ ...prev, dataBase: e.target.value }))
                      }
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground">
                      Data inicial para cálculo dos dias úteis
                    </p>
                  </div>

                  {/* Valor Aplicado */}
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <label className="text-sm font-medium">Valor Aplicado</label>
                      <span className="text-sm font-semibold">
                        {formatarMoedaCompleta(clientParams.valorAplicado)}
                      </span>
                    </div>
                    <Input
                      type="range"
                      min={1000}
                      max={10000000}
                      step={1000}
                      value={clientParams.valorAplicado}
                      onChange={(e) =>
                        handleClientParamChange("valorAplicado", parseFloat(e.target.value))
                      }
                      className="cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>R$ 1.000</span>
                      <span>R$ 10.000.000</span>
                    </div>
                  </div>

                  {/* Dias Aplicado */}
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <label className="text-sm font-medium flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Período (dias)
                      </label>
                      <span className="text-sm font-semibold">{clientParams.diasAplicado} dias</span>
                    </div>
                    <Input
                      type="range"
                      min={1}
                      max={365}
                      step={1}
                      value={clientParams.diasAplicado}
                      onChange={(e) =>
                        handleClientParamChange("diasAplicado", parseFloat(e.target.value))
                      }
                      className="cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>1 dia</span>
                      <span>1 ano</span>
                    </div>
                    {clientParams.diasAplicado <= 30 && (
                      <p className="text-xs text-yellow-600 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        IOF incide sobre CDB até 30 dias
                      </p>
                    )}
                  </div>

                  {/* % CDI Pago pela Stablecoin */}
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <label className="text-sm font-medium flex items-center gap-1">
                        <Percent className="w-4 h-4" />
                        % CDI Stablecoin
                      </label>
                      <span className="text-sm font-semibold">{clientParams.percentualCdiPago}%</span>
                    </div>
                    <Input
                      type="range"
                      min={80}
                      max={100}
                      step={1}
                      value={clientParams.percentualCdiPago}
                      onChange={(e) =>
                        handleClientParamChange("percentualCdiPago", parseFloat(e.target.value))
                      }
                      className="cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>80%</span>
                      <span>100%</span>
                    </div>
                  </div>

                  {/* Info CDI atual */}
                  <div className="border-t pt-4">
                    <div className="bg-muted p-3 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">CDI Anual (Selic)</span>
                        <span className="font-semibold">{params.cdi.toFixed(2)}%</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Ajuste na aba "Visão Operacional"
                      </p>
                    </div>
                  </div>

                  {/* Atalhos de período */}
                  <div className="border-t pt-4 space-y-3">
                    <label className="text-sm font-medium">Períodos Rápidos</label>
                    <div className="grid grid-cols-3 gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleClientParamChange("diasAplicado", 7)}
                      >
                        7 dias
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleClientParamChange("diasAplicado", 15)}
                      >
                        15 dias
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleClientParamChange("diasAplicado", 30)}
                      >
                        30 dias
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleClientParamChange("diasAplicado", 60)}
                      >
                        60 dias
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleClientParamChange("diasAplicado", 90)}
                      >
                        90 dias
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleClientParamChange("diasAplicado", 180)}
                      >
                        180 dias
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Resultados do Cliente */}
            <div className="lg:col-span-2 space-y-4">
              {clientResults && (
                <>
                  {/* Resumo Principal */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <DollarSign className="w-5 h-5" />
                        Resultado da Aplicação
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-muted p-4 rounded-lg text-center">
                          <p className="text-sm text-muted-foreground mb-1">Valor Aplicado</p>
                          <p className="text-lg font-bold">
                            {formatarMoedaCompleta(clientParams.valorAplicado)}
                          </p>
                        </div>
                        <div className="bg-muted p-4 rounded-lg text-center">
                          <p className="text-sm text-muted-foreground mb-1">Rendimento</p>
                          <p className="text-lg font-bold text-green-500">
                            +{formatarMoedaCompleta(clientResults.rendimentoBruto)}
                          </p>
                        </div>
                        <div className="bg-muted p-4 rounded-lg text-center">
                          <p className="text-sm text-muted-foreground mb-1">Valor Final</p>
                          <p className="text-lg font-bold text-green-500">
                            {formatarMoedaCompleta(clientResults.valorFinal)}
                          </p>
                        </div>
                        <div className="bg-muted p-4 rounded-lg text-center">
                          <p className="text-sm text-muted-foreground mb-1">% do CDI</p>
                          <p className="text-lg font-bold text-blue-500">
                            {clientResults.percentualDoCdi.toFixed(1)}%
                          </p>
                        </div>
                      </div>

                      {(() => {
                        const stablecoin = clientResults.produtos.find(p => p.nome === "Stablecoin Liqi");
                        const melhor = clientResults.produtos[0];
                        const diferenca = melhor && stablecoin ? melhor.rendimentoLiquido - stablecoin.rendimentoLiquido : 0;
                        const isStablecoinMelhor = clientResults.melhorProduto === "Stablecoin Liqi";

                        return (
                          <div className={`mt-4 p-4 rounded-lg ${
                            isStablecoinMelhor
                              ? "bg-green-50 dark:bg-green-900/20"
                              : "bg-yellow-50 dark:bg-yellow-900/20"
                          }`}>
                            <div className="flex items-center gap-2 mb-2">
                              {isStablecoinMelhor ? (
                                <CheckCircle className="w-5 h-5 text-green-500" />
                              ) : (
                                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                              )}
                              <span className={`font-medium ${
                                isStablecoinMelhor
                                  ? "text-green-700 dark:text-green-400"
                                  : "text-yellow-700 dark:text-yellow-400"
                              }`}>
                                Melhor opção: {clientResults.melhorProduto}
                              </span>
                            </div>
                            <p className={`text-sm ${
                              isStablecoinMelhor
                                ? "text-green-600 dark:text-green-400"
                                : "text-yellow-600 dark:text-yellow-400"
                            }`}>
                              {isStablecoinMelhor ? (
                                <>
                                  A Stablecoin Liqi oferece o melhor rendimento líquido de{" "}
                                  <strong>{formatarMoedaCompleta(clientResults.rendimentoBruto)}</strong> para o período de{" "}
                                  {clientParams.diasAplicado} dias, equivalente a{" "}
                                  <strong>{clientResults.percentualDoCdi.toFixed(1)}% do CDI</strong>.
                                  {clientParams.diasAplicado <= 30 && (
                                    <> Isso porque o CDB perderia <strong>{formatarMoedaCompleta(clientResults.produtos.find(p => p.nome === "CDB")?.iof || 0)}</strong> em IOF.</>
                                  )}
                                </>
                              ) : (
                                <>
                                  Para {clientParams.diasAplicado} dias, <strong>{clientResults.melhorProduto}</strong> rende{" "}
                                  <strong>{formatarMoedaCompleta(diferenca)}</strong> a mais que a Stablecoin.
                                  {clientParams.diasAplicado > 30 && (
                                    <> Porém, a Stablecoin oferece <strong>liquidez 24/7</strong> e rende em fins de semana.</>
                                  )}
                                  {clientParams.diasAplicado <= 30 && clientResults.melhorProduto === "Compromissada" && (
                                    <> A Compromissada não tem IOF, mas requer operação via mesa.</>
                                  )}
                                </>
                              )}
                            </p>
                            {!isStablecoinMelhor && (
                              <div className="mt-3 pt-3 border-t border-yellow-200 dark:border-yellow-800">
                                <p className="text-xs text-yellow-600 dark:text-yellow-400">
                                  <strong>Vantagens da Stablecoin mesmo não sendo a melhor em rendimento:</strong>
                                  <br />• Liquidez imediata 24/7/365 (fins de semana e feriados)
                                  <br />• Sem IOF em qualquer prazo
                                  <br />• Não depende de horário bancário ou mesa de operação
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </CardContent>
                  </Card>

                  {/* Tabela Comparativa de Produtos */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Comparativo de Produtos</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="border rounded-lg overflow-x-auto">
                        <table className="w-full min-w-[900px]">
                          <thead className="bg-muted">
                            <tr>
                              <th className="text-left p-3 font-medium text-sm">Produto</th>
                              <th className="text-right p-3 font-medium text-sm">% CDI</th>
                              <th className="text-right p-3 font-medium text-sm">
                                <div>Taxa Diária</div>
                                <div className="text-xs font-normal text-muted-foreground">Base</div>
                              </th>
                              <th className="text-right p-3 font-medium text-sm">
                                <div>Dias Rend.</div>
                                <div className="text-xs font-normal text-muted-foreground">no período</div>
                              </th>
                              <th className="text-right p-3 font-medium text-sm">Rend. Bruto</th>
                              <th className="text-right p-3 font-medium text-sm">IOF</th>
                              <th className="text-right p-3 font-medium text-sm">Rend. Líquido</th>
                              <th className="text-center p-3 font-medium text-sm">Liquidez</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {clientResults.produtos.map((produto, idx) => (
                              <tr
                                key={produto.nome}
                                className={`hover:bg-muted/50 ${
                                  produto.destaque ? "bg-green-50 dark:bg-green-900/10" : ""
                                } ${idx === 0 ? "bg-yellow-50 dark:bg-yellow-900/10" : ""}`}
                              >
                                <td className="p-3">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-sm">{produto.nome}</span>
                                    {idx === 0 && (
                                      <Badge variant="default" className="bg-yellow-500 text-xs">
                                        Melhor
                                      </Badge>
                                    )}
                                    {produto.destaque && idx !== 0 && (
                                      <Badge variant="secondary" className="text-xs">
                                        Liqi
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground">{produto.descricao}</p>
                                </td>
                                <td className="p-3 text-sm text-right">{produto.rentabilidadeCdi}</td>
                                <td className="p-3 text-sm text-right">
                                  <div className={produto.base === 365 ? "text-blue-600" : "text-orange-600"}>
                                    {(produto.taxaDiaria * 100).toFixed(6)}%
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {produto.base === 365 ? "365 dias" : "252 dias"}
                                  </div>
                                </td>
                                <td className="p-3 text-sm text-right">
                                  <div className={produto.base === 365 ? "text-blue-600 font-medium" : "text-orange-600"}>
                                    {produto.diasRendimento} dias
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {produto.base === 365 ? "todos" : "só úteis"}
                                  </div>
                                </td>
                                <td className="p-3 text-sm text-right text-green-600">
                                  +{formatarMoedaCompleta(produto.rendimentoBruto)}
                                </td>
                                <td className="p-3 text-sm text-right">
                                  {produto.iof > 0 ? (
                                    <span className="text-red-500">-{formatarMoedaCompleta(produto.iof)}</span>
                                  ) : (
                                    <span className="text-green-500">Isento</span>
                                  )}
                                </td>
                                <td className="p-3 text-sm text-right font-medium text-green-600">
                                  +{formatarMoedaCompleta(produto.rendimentoLiquido)}
                                </td>
                                <td className="p-3 text-center">
                                  <Badge
                                    variant={produto.disponibilidade === "24x7x365" ? "default" : "outline"}
                                    className={
                                      produto.disponibilidade === "24x7x365"
                                        ? "bg-green-500 text-xs"
                                        : "text-xs"
                                    }
                                  >
                                    {produto.disponibilidade === "24x7x365" ? "24/7" : "Horário"}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Legenda e Explicação */}
                      <div className="mt-4 p-4 bg-muted/50 rounded-lg space-y-3">
                        <p className="text-sm font-medium">Por que a Stablecoin pode render mais mesmo com taxa diária menor?</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                          <div className="flex items-start gap-2">
                            <div className="w-3 h-3 bg-blue-500 rounded mt-0.5 flex-shrink-0"></div>
                            <div>
                              <span className="font-medium">Base 365:</span> Taxa diária menor (~0.035%), mas rende <strong>todos os dias</strong> (incluindo fins de semana e feriados)
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <div className="w-3 h-3 bg-orange-500 rounded mt-0.5 flex-shrink-0"></div>
                            <div>
                              <span className="font-medium">Base 252:</span> Taxa diária maior (~0.05%), mas rende <strong>apenas dias úteis</strong> (~252 dias/ano)
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          <strong>Resultado:</strong> Em períodos com fins de semana, a Stablecoin "ganha" dias extras de rendimento que compensam a taxa diária menor.
                        </p>
                      </div>

                      {/* Legenda de cores */}
                      <div className="mt-4 flex flex-wrap gap-4 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 bg-yellow-100 rounded"></div>
                          <span>Melhor rendimento líquido</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 bg-green-100 rounded"></div>
                          <span>Stablecoin Liqi</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Card de Impacto do IOF */}
                  {clientParams.diasAplicado <= 30 && (
                    <Card className="border-yellow-200 bg-yellow-50/50 dark:bg-yellow-900/10">
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2 text-yellow-700 dark:text-yellow-400">
                          <AlertTriangle className="w-5 h-5" />
                          Impacto do IOF no Período de {clientParams.diasAplicado} dias
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border">
                            <p className="text-sm text-muted-foreground mb-1">Alíquota IOF</p>
                            <p className="text-2xl font-bold text-yellow-600">
                              {TABELA_IOF[clientParams.diasAplicado - 1]}%
                            </p>
                            <p className="text-xs text-muted-foreground">sobre o rendimento</p>
                          </div>
                          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border">
                            <p className="text-sm text-muted-foreground mb-1">IOF no CDB 90%</p>
                            <p className="text-2xl font-bold text-red-500">
                              -{formatarMoedaCompleta(clientResults.produtos.find(p => p.nome === "CDB")?.iof || 0)}
                            </p>
                            <p className="text-xs text-muted-foreground">perdido em impostos</p>
                          </div>
                          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border">
                            <p className="text-sm text-muted-foreground mb-1">Economia na Stablecoin</p>
                            <p className="text-2xl font-bold text-green-500">
                              +{formatarMoedaCompleta(clientResults.produtos.find(p => p.nome === "CDB")?.iof || 0)}
                            </p>
                            <p className="text-xs text-muted-foreground">sem IOF</p>
                          </div>
                        </div>

                        {/* Tabela de IOF Regressivo */}
                        <div className="mt-4">
                          <p className="text-sm font-medium mb-2">Tabela IOF Regressivo (% sobre rendimento)</p>
                          <div className="grid grid-cols-10 gap-1 text-xs">
                            {TABELA_IOF.map((aliquota, idx) => (
                              <div
                                key={idx}
                                className={`p-1 text-center rounded ${
                                  idx + 1 === clientParams.diasAplicado
                                    ? "bg-yellow-400 text-yellow-900 font-bold"
                                    : aliquota === 0
                                      ? "bg-green-100 text-green-700"
                                      : "bg-gray-100 dark:bg-gray-700"
                                }`}
                              >
                                <div className="font-medium">{idx + 1}d</div>
                                <div>{aliquota}%</div>
                              </div>
                            ))}
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">
                            Após 30 dias: IOF = 0% (isento)
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Detalhes e Vantagens */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Vantagens da Stablecoin */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2 text-green-600">
                          <CheckCircle className="w-5 h-5" />
                          Vantagens Stablecoin Liqi
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-sm">Liquidez Imediata 24/7</p>
                            <p className="text-xs text-muted-foreground">
                              Resgate a qualquer momento, inclusive fins de semana e feriados
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-sm">Sem IOF</p>
                            <p className="text-xs text-muted-foreground">
                              Diferente de CDB, não há incidência de IOF em nenhum prazo
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-sm">Rende Fins de Semana</p>
                            <p className="text-xs text-muted-foreground">
                              Base 365 dias corridos - seu dinheiro rende todos os dias
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-sm">Lastro em Títulos Públicos</p>
                            <p className="text-xs text-muted-foreground">
                              Segurança do Tesouro Direto (LFT/Selic)
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Detalhes do Cálculo */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Info className="w-5 h-5" />
                          Detalhes do Cálculo
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Data base:</span>
                          <span>{new Date(clientResults.dataBase + "T12:00:00").toLocaleDateString("pt-BR")}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Data final:</span>
                          <span>{new Date(clientResults.dataFinal + "T12:00:00").toLocaleDateString("pt-BR")}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Período total:</span>
                          <span>{clientParams.diasAplicado} dias corridos</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Dias úteis:</span>
                          <span className="font-medium text-blue-600">{clientResults.diasUteis} dias</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Fins de semana/feriados:</span>
                          <span className="text-orange-500">{clientResults.diasNaoUteis} dias</span>
                        </div>
                        <div className="border-t pt-3 flex justify-between">
                          <span className="text-muted-foreground">Taxa diária Stablecoin:</span>
                          <span>{formatarPercentual(clientResults.taxaDiaria)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Rentabilidade no período:</span>
                          <span className="text-green-500">
                            {clientResults.rentabilidadePercentual.toFixed(4)}%
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Rentabilidade anualizada:</span>
                          <span className="font-medium">
                            {clientResults.rentabilidadeAnualizada.toFixed(2)}% a.a.
                          </span>
                        </div>
                        {clientParams.diasAplicado <= 30 && (
                          <div className="border-t pt-3">
                            <div className="flex justify-between text-yellow-600">
                              <span>IOF aplicável (CDB):</span>
                              <span className="font-medium">{TABELA_IOF[clientParams.diasAplicado - 1]}%</span>
                            </div>
                          </div>
                        )}
                        {clientParams.diasAplicado > 30 && (
                          <div className="border-t pt-3">
                            <div className="flex justify-between text-green-600">
                              <span>IOF:</span>
                              <span className="font-medium">Isento (após 30 dias)</span>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Gráfico de Comparação Visual */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Comparativo Visual de Rendimento Líquido</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {clientResults.produtos.map((produto, idx) => {
                          const maxRendimento = Math.max(
                            ...clientResults.produtos.map((p) => p.rendimentoLiquido)
                          );
                          const percentWidth =
                            maxRendimento > 0
                              ? (produto.rendimentoLiquido / maxRendimento) * 100
                              : 0;

                          return (
                            <div key={produto.nome} className="space-y-1">
                              <div className="flex justify-between text-sm">
                                <span className="font-medium">{produto.nome}</span>
                                <span
                                  className={
                                    idx === 0 ? "text-green-600 font-semibold" : "text-muted-foreground"
                                  }
                                >
                                  {formatarMoedaCompleta(produto.rendimentoLiquido)}
                                </span>
                              </div>
                              <div className="h-6 bg-muted rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all duration-500 ${
                                    produto.destaque
                                      ? "bg-green-500"
                                      : idx === 0
                                        ? "bg-yellow-500"
                                        : "bg-blue-400"
                                  }`}
                                  style={{ width: `${percentWidth}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Footer */}
      <p className="text-center text-muted-foreground text-sm pt-4">
        Simulador desenvolvido para análise de viabilidade da Stablecoin BRLD.
        Os cálculos são aproximações e devem ser validados com análise financeira detalhada.
      </p>
    </div>
  );
}
